import { useEffect } from 'react';
import { Platform } from 'react-native';

interface OpenGraphMeta {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: string;
  siteName?: string;
}

interface TwitterMeta {
  card?: 'summary' | 'summary_large_image' | 'app' | 'player';
  title?: string;
  description?: string;
  image?: string;
}

interface HeadMetaProps {
  title?: string;
  description?: string;
  keywords?: string;
  openGraph?: OpenGraphMeta;
  twitter?: TwitterMeta;
  canonicalUrl?: string;
}

export const useHeadMeta = (props: HeadMetaProps) => {
  const { title, description, keywords, openGraph, twitter, canonicalUrl } =
    props;

  useEffect(() => {
    if (Platform.OS !== 'web') return;

    // Update document title
    if (title && typeof document !== 'undefined') {
      document.title = title;
    }

    // Function to update or create meta tag
    const updateMetaTag = (
      name: string,
      content: string,
      isProperty = false,
    ) => {
      if (typeof document === 'undefined') return;

      const attribute = isProperty ? 'property' : 'name';
      let meta = document.querySelector(
        `meta[${attribute}="${name}"]`,
      ) as HTMLMetaElement;

      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }

      meta.content = content;
    };

    // Update or create link tag
    const updateLinkTag = (rel: string, href: string) => {
      if (typeof document === 'undefined') return;

      let link = document.querySelector(
        `link[rel="${rel}"]`,
      ) as HTMLLinkElement;

      if (!link) {
        link = document.createElement('link');
        link.rel = rel;
        document.head.appendChild(link);
      }

      link.href = href;
    };

    // Basic meta tags
    if (description) updateMetaTag('description', description);
    if (keywords) updateMetaTag('keywords', keywords);
    if (canonicalUrl) updateLinkTag('canonical', canonicalUrl);

    // OpenGraph meta tags
    if (openGraph?.title) updateMetaTag('og:title', openGraph.title, true);
    if (openGraph?.description)
      updateMetaTag('og:description', openGraph.description, true);
    if (openGraph?.image) updateMetaTag('og:image', openGraph.image, true);
    if (openGraph?.url) updateMetaTag('og:url', openGraph.url, true);
    if (openGraph?.type) updateMetaTag('og:type', openGraph.type, true);
    if (openGraph?.siteName)
      updateMetaTag('og:site_name', openGraph.siteName, true);

    // Twitter Card meta tags
    if (twitter?.card) updateMetaTag('twitter:card', twitter.card);
    if (twitter?.title) updateMetaTag('twitter:title', twitter.title);
    if (twitter?.description)
      updateMetaTag('twitter:description', twitter.description);
    if (twitter?.image) updateMetaTag('twitter:image', twitter.image);
  }, [title, description, keywords, openGraph, twitter, canonicalUrl]);

  // This hook doesn't return JSX, it just updates the DOM directly on web
  return null;
};

export default useHeadMeta;
